import type { DataRequirements } from './plan';
import type { Concepts } from './screenIndex';
import type { FailureCause } from './failure';
import type { Shape } from './compute';

/**
 * 다 만든 값이 질문의 답이 되는지 본다.
 *
 * 오류가 하나도 나지 않고 틀리는 일이 있다. `수금은 잘 되고 있어?` 가 `0원` 이라고
 * 답했을 때 조회도 계산도 성공했고 답변도 멀쩡했다 — 년합계 열이 비어 있었고 파이썬이
 * 그것을 더했을 뿐이다. 아무도 "이게 답이 되나" 를 묻지 않아서 그대로 나갔다.
 *
 * 여기서 거절하면 그것은 **다시 해볼 수 있는 실패**가 된다. 답이 아닌 것을 답인 척
 * 내보내는 것보다 낫다.
 *
 * 재는 것은 관찰할 수 있는 것뿐이다. `매출이 88,000원이면 너무 작다` 같은 판단은
 * 넣지 않는다 — 진짜로 작은 달이 있고, 그것을 틀렸다고 하면 맞는 답을 버린다.
 */
export type VerifyReport = {
  ok: boolean;
  cause?: FailureCause;
  /** 다시 계산할 때 모델에게 그대로 넘길 말. 사용자에게 보이는 문구가 아니다. */
  reason?: string;
};

/** 값 안의 숫자를 모은다. 깊이와 개수를 묶어 큰 결과에서도 값싸게 끝낸다. */
function numbersIn(value: unknown): number[] {
  const found: number[] = [];
  const walk = (node: unknown, depth: number): void => {
    if (depth > 4 || found.length > 200) return;
    if (typeof node === 'number') { found.push(node); return; }
    if (typeof node === 'string') {
      const parsed = Number(node.replace(/,/g, '').trim());
      if (node.trim() !== '' && Number.isFinite(parsed)) found.push(parsed);
      return;
    }
    if (Array.isArray(node)) { node.forEach((item) => walk(item, depth + 1)); return; }
    if (node && typeof node === 'object') Object.values(node).forEach((item) => walk(item, depth + 1));
  };
  walk(value, 0);
  return found;
}

/** 결과가 몇 갈래로 나뉘어 있는가. 분류축을 요구한 질문이 한 줄로 끝나면 안 된다. */
function branches(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object') {
    const table = value as { rows?: unknown[]; table?: { rows?: unknown[] } };
    if (Array.isArray(table.rows)) return table.rows.length;
    // 앱 쪽 TS 는 `table.table?.rows` 를 좁혀 주지 않는다. 받아서 재는 편이 싸다.
    const nested = table.table?.rows;
    if (Array.isArray(nested)) return nested.length;
    const lists = Object.values(value).filter(Array.isArray) as unknown[][];
    if (lists.length) return Math.max(...lists.map((list) => list.length));
  }
  return 1;
}

/** 값이 하나도 없다고 표시된 열들. `shapesOf` 가 붙여 둔 꼬리표를 읽는다. */
function emptyColumnIds(shapes: Shape[]): string[] {
  return shapes.flatMap((shape) => shape.columns
    .filter((column) => /값 없음/.test(column.text ?? ''))
    .map((column) => column.id));
}

/**
 * 좁힌 기간 밖의 행이 대부분인가.
 *
 * `이번 달 만료되는 보수계약 있어?` 가 13,828곳이라고 답했다. 그 화면을 같은 조건으로
 * 직접 조회하면 마흔여섯 행이다 — 조건이 걸리기 전 결과를 읽은 것이다. 조회가 성공으로
 * 보이고 행도 많아서 아무도 의심하지 않았다.
 *
 * 조건에 여덟 자리 날짜가 둘 있으면 그것이 범위다. 행에서 같은 꼴의 값을 찾아 견준다.
 */
function outsidePeriod(
  conditions: Record<string, string>,
  rows: Record<string, unknown>[],
): boolean {
  const bounds = Object.values(conditions)
    .map((value) => String(value).trim())
    .filter((value) => /^\d{8}$/.test(value))
    .sort();
  if (bounds.length < 2 || !rows.length) return false;
  const from = bounds[0]!;
  const to = bounds[bounds.length - 1]!;
  const dated = rows.filter((row) => Object.values(row)
    .some((cell) => /^\d{8}$/.test(String(cell ?? '').trim())));
  if (dated.length < rows.length / 2) return false;
  const outside = dated.filter((row) => !Object.values(row).some((cell) => {
    const text = String(cell ?? '').trim();
    return /^\d{8}$/.test(text) && text >= from && text <= to;
  }));
  return outside.length > dated.length / 2;
}

/** 질문이 한 해보다 좁은 기간을 가리키는가. */
function narrowPeriod(question: string): boolean {
  return /이번\s*주|이번\s*달|이달|금월|지난\s*달|전월|주간|월간|\d{1,2}\s*월/.test(question);
}

/**
 * 월이 여럿 담긴 표를 받고도 계산이 **월 열을 아예 안 봤는가.**
 *
 * `이번 달 매출이랑 매입 비교해줘` 가 연매출 130억을 냈다. 화면(월매출매입현황)은
 * 202601~202610 을 월별로 주는데 계산은 열두 달을 다 더했고, 이름만 `연매출` 이라
 * 붙여 검증도 통과했다 — 8월 값(4.5억)이 자료 안에 있었는데도.
 *
 * 이름을 붙였는지로는 못 가린다. 무엇을 안 했는지가 관찰할 수 있는 것이다:
 * 여섯 자리 월 열에 서로 다른 달이 둘 이상 있는데 코드가 그 열 이름을 한 번도 쓰지
 * 않았다면, 기간을 거른 적이 없다는 뜻이다.
 */
function unfilteredMonths(
  retrievals: { rows: Record<string, unknown>[] }[],
  code: string,
): string | undefined {
  for (const retrieval of retrievals) {
    const rows = retrieval.rows;
    if (rows.length < 2) continue;
    for (const id of Object.keys(rows[0] ?? {})) {
      const values = rows.map((row) => String(row[id] ?? '').trim()).filter(Boolean);
      if (values.length < rows.length) continue;
      if (!values.every((value) => /^\d{6}$/.test(value))) continue;
      if (new Set(values).size < 2) continue;
      if (new RegExp(`['"\`]${id}['"\`]`).test(code)) continue;
      return id;
    }
  }
  return undefined;
}

/**
 * 달이 **열 이름에 있는** 표에서 계산이 월별 열을 안 봤는가.
 *
 * 같은 병의 두 번째 모양이다. `월매출매입현황` 의 사업장별 표는 행이 사업장이고
 * 달은 `misamt01`…`misamt12` 처럼 열 이름에 붙어 있다. 계산은 년합계 열 `misamt` 를
 * 집어 이번 달 보고서에 연매출 130억을 실었다 — 8월 열이 같은 행에 있었는데도.
 *
 * 접미사 두 자리가 01~12 로 여럿인 열 무리를 찾는다. 그 무리를 코드가 하나도 안
 * 썼다면 달을 고른 적이 없다는 뜻이다.
 */
function unusedMonthColumns(
  retrievals: { rows: Record<string, unknown>[] }[],
  code: string,
): string | undefined {
  for (const retrieval of retrievals) {
    const ids = Object.keys(retrieval.rows[0] ?? {});
    const families = new Map<string, Set<string>>();
    for (const id of ids) {
      const parts = /^([a-z][a-z_]*?)(0[1-9]|1[0-2])$/i.exec(id);
      if (!parts) continue;
      const family = families.get(parts[1]!) ?? new Set<string>();
      family.add(id);
      families.set(parts[1]!, family);
    }
    for (const [prefix, members] of Array.from(families.entries())) {
      if (members.size < 6) continue;
      // **두 자리 접미사가 늘 달은 아니다.** 점검자별 현황은 `ju01`~`ju31` 로 **날짜**를
      // 편다. 13 이 넘는 형제가 있으면 달 무리가 아니다 — 그것을 달로 보고 거절하는
      // 바람에 맞는 답에 "화면 자료와 맞지 않을 수 있습니다" 가 붙었다.
      const daily = ids.some((id) => new RegExp(`^${prefix}(1[3-9]|2\d|3[01])$`, 'i').test(id));
      if (daily) continue;
      // 무리 전체가 비어 있으면 쓸 수 없는 열이다. `iamt(년합계)` 처럼 반대 경우가 있어
      // 값이 하나라도 있는 무리만 따진다.
      const filled = Array.from(members).some((id) => retrieval.rows
        .some((row) => String(row[id] ?? '').trim() !== ''));
      if (!filled) continue;
      if (Array.from(members).some((id) => new RegExp(`['"\`]${id}['"\`]`).test(code))) continue;
      return `${prefix}01~${prefix}12`;
    }
  }
  return undefined;
}

/**
 * 사람이 쓰는 축의 말과 화면의 열 이름이 늘 같지는 않다.
 *
 * `지점` 을 물어도 화면은 `사업장명` 이라 적는다. 낱말이 다르다고 없는 축이라 하면
 * 맞는 답을 되돌리게 된다.
 */
const AXIS_WORDS: Record<string, string[]> = {
  지점: ['지점', '사업장', '지사', '사업소'],
  사업장: ['사업장', '지점', '지사', '사업소'],
  현장: ['현장', '공사현장', '설치현장'],
  부서: ['부서', '소속', '팀'],
  담당자: ['담당자', '담당', '사원', '직원', '사용자', '성명', 'pernm'],
  // 사람을 부르는 말은 화면마다 다르다. 점검자별 현황의 열 이름은 `사용자`(`pernm`)다 —
  // `점검자` 라는 낱말이 없다고 "그 축이 없다" 며 맞는 답을 되돌린 적이 있다.
  점검자: ['점검자', '담당', '사원', '직원', '사용자', '성명', 'pernm'],
  작업자: ['작업자', '담당', '사원', '직원', '사용자', '성명', 'pernm'],
  사원: ['사원', '직원', '담당', '사용자', '성명', 'pernm'],
  직원: ['직원', '사원', '담당', '사용자', '성명', 'pernm'],
  거래처: ['거래처', '매출처', '매입처', '고객'],
};

/**
  * 조회한 자료에 그 축으로 쓸 열이 실제로 있는가.
  *
  * **`concept` 을 먼저 보되 낱말 표를 지우지 않는다.** 규칙으로 딱지가 붙은 것은
  * 열 2,811개 중 1,669개(59%)다. 59% 짜리 표로 낱말 매칭을 대체하면 나머지 41%가
  * 조용히 죽는다 — 그 자리는 「축이 없다」며 맞는 답을 되돌리던 바로 그 자리다.
  */
function hasAxisColumn(shapes: Shape[], axis: string, concepts?: Concepts): boolean {
  const wanted = conceptsFor(axis, concepts);
  if (wanted.length && shapes.some((shape) => shape.columns.some(
    (column) => column.concept && wanted.includes(column.concept),
  ))) return true;
  const words = AXIS_WORDS[axis] ?? [axis];
  return shapes.some((shape) => shape.columns.some((column) => {
    const text = `${column.text ?? ''} ${column.id}`;
    return words.some((word) => text.includes(word));
  }));
}

/**
 * 사람이 말한 축 낱말이 어느 `concept` 인가. 이름 변형(`site.name`)까지 함께 본다 —
 * 화면은 `현장코드` 와 `현장명` 을 따로 두는데 사람에게는 둘 다 「현장」이다.
 */
function conceptsFor(axis: string, concepts: Concepts | undefined): string[] {
  if (!concepts) return [];
  const out: string[] = [];
  for (const [name, words] of Object.entries(concepts)) {
    if (words.some((word) => axis.includes(word) || word.includes(axis))) {
      out.push(name, `${name}.name`);
    }
  }
  return out;
}

export function verifyResult(input: {
  value: unknown;
  code: string;
  shapes: Shape[];
  requirements?: DataRequirements;
  /** 사람이 물은 말. 순위·목록을 요구했는지는 여기서만 알 수 있다. */
  question?: string;
  rowsRetrieved: number;
  /** 화면이 따로 내놓는 합계 값들. 행이 없어도 이것만으로 답이 되는 화면이 있다. */
  totals?: Record<string, Record<string, unknown>>;
  /** 조회한 조건과 그 결과 행. 조건이 정말 걸렸는지 보는 데 쓴다. */
  retrievals?: { conditions: Record<string, string>; rows: Record<string, unknown>[] }[];
  /** 사람이 말한 낱말 → 열의 뜻. 없으면 예전처럼 낱말 표만 쓴다. */
  concepts?: Concepts;
}): VerifyReport {
  const {
    value, code, shapes, requirements, question, rowsRetrieved, totals, retrievals, concepts,
  } = input;
  if (!rowsRetrieved) {
    // 행이 없고 합계도 전부 0 이면 그 기간에 자료가 없는 것이다. 합계 칸은 자료가
    // 없어도 `0` 으로 존재해서, 그것을 결과로 인정해 `수금 0원` 이라고 답한 적이 있다 —
    // 그것은 답이 아니라 자료가 없다는 뜻이었다.
    const figures = Object.values(totals ?? {}).flatMap((one) => numbersIn(one));
    if (figures.length > 0 && figures.every((figure) => figure === 0)) {
      return {
        ok: false,
        cause: 'empty-result',
        reason: '조회한 표에 행이 없고 화면 합계도 전부 0 이다. 그 기간에 자료가 없다는 뜻이니'
          + ' 0 을 실적으로 적지 마라.',
      };
    }
    return { ok: true };
  }

  // 조건이 걸리기 전 결과를 읽었는가. 행이 많다고 조회가 된 것은 아니다.
  //
  // **계산이 날짜로 이미 걸렀으면 이 규칙은 할 일이 없다.** 기간을 무시하는 화면
  // (지출결의서)에서는 이 규칙이 영원히 참이라, 맞게 걸러 낸 답에도 `화면 자료와 맞지
  // 않을 수 있습니다` 가 계속 붙었다.
  // 날짜 열을 **입에 올린 것**과 그것으로 **거른 것**은 다르다. 열 이름만 보면
  // 결과에 날짜를 실어 보내는 계산까지 통과시켜, 조건이 안 걸린 조회를 놓친다.
  const filteredByDate = /(date|일자)/i.test(code)
    && /(\[:\s*\d|startswith|>=|<=|==|!=|<|>)/.test(code);
  if (!filteredByDate && (retrievals ?? []).some((one) => outsidePeriod(one.conditions, one.rows))) {
    return {
      ok: false,
      cause: 'useless-result',
      reason: '조회 결과의 날짜가 요청한 기간 밖이다. **기간 조건을 무시하는 화면이 있다** —'
        + ' 지출결의서는 기간을 걸어도 지난달 문서를 함께 준다. 다시 조회하지 말고'
        + ' 날짜 열로 그 기간의 행만 걸러서 계산하라. 그렇게 걸러 한 건도 안 남으면'
        + ' 그 기간에는 없는 것이다.',
    };
  }

  // 물은 기간은 이번 달인데 계산이 월 열을 안 봤다. 열두 달을 더한 값이 나간다.
  if (narrowPeriod(question ?? '')) {
    const month = unfilteredMonths(retrievals ?? [], code);
    if (month) {
      return {
        ok: false,
        cause: 'useless-result',
        reason: `조회 결과의 ${month} 열에 서로 다른 달이 섞여 있는데 계산이 그 열을 쓰지 않았다.`
          + ' 질문이 가리키는 달의 행만 남겨 계산하라 — 열두 달을 더해 놓고 이름만'
          + ' `연매출` 이라 붙이지 마라. 그 달의 값이 자료에 있다.',
      };
    }
    const columns = unusedMonthColumns(retrievals ?? [], code);
    if (columns) {
      return {
        ok: false,
        cause: 'useless-result',
        reason: `조회 결과에 월별 열 ${columns} 이 있는데 계산이 하나도 쓰지 않았다.`
          + ' 질문이 가리키는 달의 열로 계산하라 — 년합계 열을 집어 놓고 이번 달이라고'
          + ' 하지 마라.',
      };
    }
  }

  const numbers = numbersIn(value);
  const anyFigure = numbers.some((number) => number !== 0);

  // **화면이 못 채운 합계 칸을 계산이 읽었다.** 행은 왔는데 건수 칸이 비어 「자료가 없다」고 답했다(점검결과등록,
  // 사용자 2026-10-15). 그 칸 대신 행에서 세게 한다. 결과에 값이 나왔으면 행으로 센 것이니 두다.
  const blankTotals = shapes.flatMap((shape) => (shape.scalars ?? []).filter((one) => one.blank).map((one) => one.key))
    .filter((key) => new RegExp(`['"\`]${key}['"\`]`).test(code));
  if (blankTotals.length && !anyFigure) {
    return {
      ok: false,
      cause: 'empty-column',
      reason: `화면 합계 칸 ${blankTotals.join(', ')} 이 비어 있는데(화면이 못 채움) 계산이 그것을 썼다.`
        + ` 조회한 행이 ${rowsRetrieved}건 있다 — 그 칸을 쓰지 말고 행에서 직접 세거나 더해라.`,
    };
  }

  // 값이 하나도 없는 열을 계산이 집었다. 이것이 `0원` 의 정체였다.
  //
  // 다만 **결과에 값이 나왔으면 트집 잡지 않는다.** 빈 열을 대조용으로만 읽고 합계는
  // 다른 열로 내는 계산이 있다 — 그것까지 거절하면 맞는 답에 경고를 붙이게 된다.
  const blank = emptyColumnIds(shapes).filter((id) => new RegExp(`['"\`]${id}['"\`]`).test(code));
  if (blank.length && !anyFigure) {
    return {
      ok: false,
      cause: 'empty-column',
      reason: `${blank.join(', ')} 컬럼은 값이 전부 비어 있는데 계산이 그것을 썼다.`
        + ' 같은 뜻을 담은 다른 컬럼으로 계산하라 — 년합계가 비면 월별 값을 더한다.',
    };
  }

  if (numbers.length > 0 && !anyFigure) {
    return {
      ok: false,
      cause: 'useless-result',
      reason: '계산 결과의 숫자가 전부 0 이라 답이 되지 못했다.'
        + ' 비어 있는 열 대신 값이 있는 열로 다시 계산하라.',
    };
  }

  // `거래처별` 을 물었는데 사업장으로 묶어 놓고 이름만 거래처라 붙인 적이 있다.
  // 천삼백여든여섯 행에 거래처가 일곱 곳일 리 없다. 물은 축이 표의 열에 있어야 한다.
  const axis = /([가-힣]{2,6})\s*별/.exec(question ?? '')?.[1];
  const columns = (value as { table?: { columns?: unknown[] } })?.table?.columns ?? [];
  if (axis && columns.length && !columns.some((column) => String(column).includes(axis))) {
    return {
      ok: false,
      cause: 'useless-result',
      reason: `${axis}별로 나눠 달라고 했는데 결과 표에 ${axis} 열이 없다.`
        + ` 조회한 행에서 ${axis}에 해당하는 컬럼으로 묶어라 — 다른 축으로 묶고 이름만 바꿔 붙이지 마라.`,
    };
  }

  // 반대 방향도 있다. **결과 표에는 그 이름이 있는데 조회한 자료에는 그런 열이 없는**
  // 경우다. `이번 주 지점별 매출` 이 그랬다 — 매출현황에는 현장명뿐이고 지점·사업장
  // 열이 없는데, 계산이 현장으로 묶고 열 이름만 `지점` 이라 붙여 위 검사를 통과했다.
  // 사장님 주간보고에 현장 백열아홉 곳이 지점으로 실려 나갔다.
  if (axis && columns.length && !hasAxisColumn(shapes, axis, concepts)) {
    return {
      ok: false,
      cause: 'useless-result',
      reason: `조회한 자료에 ${axis}에 해당하는 열이 없다. 없는 축을 만들어 붙이지 마라 —`
        + ` 자료에 있는 축으로 묶고, ${axis} 자료가 없다는 사실과 무엇으로 나눴는지를`
        + ' 결과 이름에 적어라.',
    };
  }

  // `지점별`, `담당자별` 을 물었는데 한 줄로 답했다. 조회는 여러 행을 가져왔는데도.
  //
  // 계획이 dimensions 를 안 채우는 턴이 있어서 질문 자체도 본다 — `미수금이 많은 현장
  // 알려줘` 에 "73곳, 합계 78억" 만 답한 적이 있다. 곳수와 합계는 물은 것이 아니다.
  const namesSomething = /많은|적은|누가|누구|어디|순위|상위|하위|별로|best|top/i.test(question ?? '');
  const wantsBreakdown = (requirements?.dimensions ?? []).length > 0 || namesSomething;
  // **표로 답했으면 나눈 것이다.** 조건에 맞는 것이 한 건뿐일 수도 있다 —
  // `덕봉빌딩 소음 민원 어떻게 처리됐어?` 에 접수·처리를 이어 붙인 한 줄짜리 표를
  // 냈는데 "나눠 달라고 했는데 한 갈래뿐이다" 로 거절당했고, 사용자는 맞는 답에
  // `화면 자료와 맞지 않을 수 있습니다` 라는 경고가 붙은 것을 봤다.
  const tabled = Boolean((value as { table?: { rows?: unknown[] } })?.table?.rows?.length);
  if (wantsBreakdown && !tabled && rowsRetrieved > 1 && branches(value) <= 1) {
    return {
      ok: false,
      cause: 'useless-result',
      reason: '나눠 달라고 했는데 결과가 한 갈래뿐이다. 합계만으로는 답이 되지 않는다 —'
        + ' 조회한 행을 그 기준으로 묶어 이름과 값을 담은 목록으로 내라.',
    };
  }

  return { ok: true };
}
